import { useReducer, useRef } from 'react';
import { useIntl } from 'react-intl';

import { FREE_TEXT_ADDRESS_SEARCH_ENABLED } from '../../../../config/featureToggles/constants';
import { isEnabled } from '../../../../config/featureToggles/utils';
import { useGlobalConfig } from '../../../../hooks/configContext';
import { Cloud$SuggestedAddress } from '../../../../types/cloudApi';

import { BusinessUnitConfig } from '../../../../cloudConfig/cloudConfig';
import { getMessageForFetchError } from '../ErrorHandling/errorMessageHelpers';
import { validateOnClient, validateOnServer } from './addressValidationUtils';
import { ValidationState, reducer } from './reducer';
import { DropLast } from './typeUtils';

type Options = {
  freeTextSearchEnabled?: boolean;
  buSlug: BusinessUnitConfig['slug'];
  market: BusinessUnitConfig['market'];
};

async function validateAddress(
  inputAddress: Cloud$SuggestedAddress,
  suggestions: Cloud$SuggestedAddress[] | null,
  target: 'client' | 'server' = 'client',
  options: Options,
) {
  const clientValidatedAddress = validateOnClient(
    inputAddress,
    suggestions,
    !!options.freeTextSearchEnabled,
  );

  if (target !== 'server') {
    return clientValidatedAddress;
  }

  return validateOnServer(
    clientValidatedAddress,
    options.buSlug,
    options.market,
  );
}

type ValidationActions = {
  resetError: () => void;
  validate: (
    // The validation function have the same arguments as `validateAddress()`,
    // except from the last argument `options`
    ...args: DropLast<Parameters<typeof validateAddress>>
  ) => Promise<Cloud$SuggestedAddress | void>;
};

export function useAddressValidation(): {
  state: ValidationState;
  actions: ValidationActions;
} {
  const intl = useIntl();
  const config = useGlobalConfig();

  const [state, dispatch] = useReducer(reducer, {
    status: 'idle',
    errorMessage: null,
  });

  const lastValidatedAddressRef = useRef<Cloud$SuggestedAddress | null>(null);

  const validate = async (
    addressToValidate: Cloud$SuggestedAddress,
    suggestions: Cloud$SuggestedAddress[] | null,
    target: 'client' | 'server' = 'client',
  ) => {
    // Check if we already validated this exact address
    const lastValidated = lastValidatedAddressRef.current;
    if (
      state.status === 'valid' &&
      lastValidated &&
      lastValidated.text === addressToValidate.text &&
      lastValidated.id === addressToValidate.id
    ) {
      // Address is already validated and hasn't changed, return it
      return lastValidated;
    }

    // Check if we're trying to validate an address that previously failed
    if (
      state.status === 'invalid' &&
      lastValidated &&
      lastValidated.text === addressToValidate.text &&
      lastValidated.id === addressToValidate.id
    ) {
      // Don't spam the server with the same invalid address
      return null;
    }

    dispatch({ type: 'IS_VALIDATING' });

    const options = {
      freeTextSearchEnabled: isEnabled(
        FREE_TEXT_ADDRESS_SEARCH_ENABLED,
        config,
      ),
      buSlug: config.BU_CONFIG.slug,
      market: config.BU_CONFIG.market,
    };

    try {
      const validAddress = await validateAddress(
        addressToValidate,
        suggestions,
        target,
        options,
      );

      // Store the successfully validated address
      lastValidatedAddressRef.current = validAddress;

      dispatch({
        type: 'VALIDATION_SUCCESS',
      });
      return validAddress;
    } catch (error) {
      // Store the failed address to avoid re-trying
      lastValidatedAddressRef.current = addressToValidate;

      dispatch({
        type: 'VALIDATION_FAILED',
        errorMessage: intl.formatMessage(getMessageForFetchError(error)),
      });
      return null;
    }
  };

  function resetError() {
    // Clear the cached address when resetting error
    lastValidatedAddressRef.current = null;
    dispatch({ type: 'RESET_ERROR' });
  }

  const actions = {
    validate,
    resetError,
  };

  return { state, actions };
}
